import { type ComponentProps, createContext, useContext } from 'react';
import { Item, Root } from '@radix-ui/react-toggle-group';

import { cn } from '@/util/utils';

/**
 * The prototype has no shared segmented control, only two looks written by hand per screen, so
 * this is one component with both (ALS-275):
 *
 * - `tray`, the tab strip's pill (.cd-seg): a muted, bordered track with 4px in and between, the
 *   chosen segment white with a hairline, the labels in the 12px label style. Adjust wallet, the
 *   wardrobe's source filter, the outfit picker's source. The two .cd-seg screens agree on 500 and
 *   28, which is this; the outfit picker draws its labels at 400 and 32 tall, and sets that itself.
 * - `solid` (.sub-picker__src): a hairline pill with 2px in and between and 12px Geist labels, the
 *   chosen segment filled in primary. The piece editor's "Add from".
 *
 * One of the segments is always chosen: clicking the chosen one again does nothing.
 */
type Tone = 'tray' | 'solid';

const ToneContext = createContext<Tone>('tray');

type SegmentedControlProps = Omit<
    ComponentProps<typeof Root>,
    'type' | 'value' | 'defaultValue' | 'onValueChange'
> & {
    tone?: Tone;
    value: string;
    onValueChange: (value: string) => void;
};

function SegmentedControl({
    tone = 'tray',
    value,
    onValueChange,
    className,
    children,
    ref,
    ...props
}: SegmentedControlProps) {
    return (
        <Root
            data-slot="segmented-control"
            data-tone={tone}
            type="single"
            value={value}
            onValueChange={(next) => {
                if (next !== '') onValueChange(next);
            }}
            className={cn(
                'inline-flex max-w-full items-center self-start',
                tone === 'tray'
                    ? 'bg-muted border-border gap-1 rounded-lg border p-1'
                    : 'border-border gap-0.5 rounded-full border p-0.5',
                className,
            )}
            ref={ref}
            {...props}
        >
            <ToneContext.Provider value={tone}>{children}</ToneContext.Provider>
        </Root>
    );
}

function SegmentedControlItem({ className, ref, ...props }: ComponentProps<typeof Item>) {
    const tone = useContext(ToneContext);
    return (
        <Item
            data-slot="segmented-control-item"
            className={cn(
                'text-muted-foreground inline-flex min-w-0 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap outline-none pointer-coarse:min-h-11',
                'focus-visible:outline-ring transition-colors duration-(--duration-fast) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
                'disabled:pointer-events-none disabled:opacity-50',
                tone === 'tray'
                    ? 'text-label-md data-[state=on]:bg-card data-[state=on]:border-border data-[state=on]:text-foreground rounded-md border border-transparent px-2.5 py-[5px]'
                    : 'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-full px-3 py-1 text-xs leading-5',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

export { SegmentedControl, SegmentedControlItem };
